import { plainTextToRichText } from '@app/formats'
import { appKey } from '@app/shared/brand'
import type { RouterOutputs } from './trpc'

/**
 * Handing a composed message to the composer.
 *
 * The assistant reads an invitation on one screen and the message is written on
 * another, so something has to carry it across. Not the URL — an announcement is
 * paragraphs, and a query string is the wrong size and the wrong place for
 * words a family wrote.
 *
 * It is a ONE-SHOT: taking a draft clears it. Otherwise navigating back to the
 * composer later would silently refill a form somebody had deliberately
 * emptied, which is the kind of bug that reads as the app having a memory of
 * its own.
 *
 * `sessionStorage` rather than a module variable so a reload mid-handoff does
 * not lose what was read — the model call cost real money and a refresh should
 * not spend it again.
 */

const KEY = appKey('message-draft')

type Composed = NonNullable<
  RouterOutputs['messaging']['history'][number]['composed']
>

/**
 * The settings a draft arrives with. Read off what the history returns, so a
 * setting a send records is one a duplicate carries, with no second list of
 * them to keep in step. `listId` is the record's own column beside them.
 */
export type MessageDraftSettings = Partial<Composed['settings']> & {
  listId: string | null
}

export type MessageDraft = {
  subject: string
  /** The email body as HTML, for the rich editor. */
  bodyHtml: string
  /** The same body as plain text — the email's text part and the SMS fallback. */
  bodyText: string
  sms: string
  /** Where it came from, so the composer can say so rather than appearing psychic. */
  source: string
  /**
   * The conversation that produced it, so that actually sending the message can
   * be reported back as the assistant having been USED. Without this the record
   * stops at "they opened the composer", which is not the same thing.
   */
  conversationId?: string
  /** A recovery draft opened after its scheduled send already happened. */
  lateSend?: boolean
  settings?: MessageDraftSettings
}

/**
 * Plain lines → paragraphs, for anything composed here that is not a simcha.
 * A read invitation uses `simchaBodyHtml`, which knows about direction and
 * centering; this is the fallback for plain text.
 */
export const linesToHtml = plainTextToRichText

/** What a draft is made from: one send, as the history returns it. */
type SentRecord = Pick<
  RouterOutputs['messaging']['history'][number],
  | 'subject'
  | 'body'
  | 'bodyHtml'
  | 'smsBody'
  | 'listId'
  | 'composed'
  | 'luachMissing'
>

/**
 * "Duplicate as draft": the draft a send's record opens in the composer.
 *
 * A send that went WITHOUT its luach makes the draft that sends it after. It
 * goes by email only, since nobody reached by text was owed a luach, and it
 * keeps the day the lost luach was for, so it attaches that day's sheet
 * rather than the occasion's next. Any other send duplicates as it was
 * composed, and attaches the occasion's next luach as a new message would.
 *
 * `sentOn` is when the send went, as the record states it.
 */
export function draftFromSend(
  message: SentRecord,
  sentOn: string,
): MessageDraft {
  const content = message.composed ?? message
  const base = {
    subject: content.subject,
    bodyHtml: content.bodyHtml ?? linesToHtml(content.body),
    bodyText: content.body,
    sms: content.smsBody ?? '',
  }
  const composed = message.composed?.settings
  if (!composed) {
    return {
      ...base,
      source: `a message sent on ${sentOn}; its settings were not kept`,
      settings: { listId: message.listId },
    }
  }
  if (message.luachMissing && composed.attachOccasionKey) {
    return {
      ...base,
      source: `a message sent on ${sentOn} that went without its luach. This sends that luach after it, by email only, and is a second message to everyone on the list who takes email`,
      lateSend: true,
      settings: {
        ...composed,
        listId: message.listId,
        allowedChannels: ['email'],
        deliveryMode: 'all',
      },
    }
  }
  const { attachOccurrence: _day, ...settings } = composed
  return {
    ...base,
    source: `a message sent on ${sentOn}`,
    settings: { ...settings, listId: message.listId },
  }
}

export function stageDraft(draft: MessageDraft): void {
  sessionStorage.setItem(KEY, JSON.stringify(draft))
}

/** Read it once. A second call returns nothing. */
export function takeDraft(): MessageDraft | null {
  const raw = sessionStorage.getItem(KEY)
  if (!raw) return null
  sessionStorage.removeItem(KEY)
  try {
    return JSON.parse(raw) as MessageDraft
  } catch {
    return null
  }
}
